import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import AmountInput from './ui/AmountInput';
import { toast } from 'sonner';
import { formatAdvanceNumber } from '../utils/formatters';
import Tooltip from './ui/simple-tooltip';
import { 
  CheckIcon, 
  XIcon, 
  ClockIcon, 
  CalendarIcon,
  Banknote as BanknoteIcon,
  FolderIcon,
  UserIcon,
  MessageSquareIcon,
  RefreshCwIcon,
  EditIcon,
  TrashIcon,
  FilterIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SearchIcon as MagnifyingGlassIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PendingApprovals = () => {
  const { user } = useAuth();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [approvalAmount, setApprovalAmount] = useState('');
  const [managerNotes, setManagerNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: '',
    requester: '',
    project: '',
    category: '',
    urgency: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    amount: '',
    currency: 'TRY',
    category: '',
    project: '',
    description: '',
    closure_days: 30
  });

  // Fetch pending advance requests
  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/advance-requests/pending`);
      setPendingRequests(response.data);
      setFilteredRequests(response.data);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      toast.error('Onay bekleyen talepler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, searchTerm, pendingRequests]);

  // Apply filters
  const applyFilters = () => {
    if (!pendingRequests.length) return;

    let filtered = [...pendingRequests];

    // Apply search term
    if (searchTerm) {
      filtered = filtered.filter(req => 
        req.requester_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.project?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.advance_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(req => {
        const requestDate = new Date(req.request_date);
        const fromDate = new Date(filters.dateFrom);
        return requestDate >= fromDate;
      });
    }
    if (filters.dateTo) {
      filtered = filtered.filter(req => {
        const requestDate = new Date(req.request_date);
        const toDate = new Date(filters.dateTo);
        return requestDate <= toDate;
      });
    }

    // Amount range filter
    if (filters.minAmount) {
      filtered = filtered.filter(req => req.amount >= parseFloat(filters.minAmount));
    }
    if (filters.maxAmount) {
      filtered = filtered.filter(req => req.amount <= parseFloat(filters.maxAmount));
    }

    // Requester filter
    if (filters.requester) {
      filtered = filtered.filter(req => 
        req.requester_name?.toLowerCase().includes(filters.requester.toLowerCase())
      );
    }

    // Project filter
    if (filters.project) {
      filtered = filtered.filter(req => 
        req.project?.toLowerCase().includes(filters.project.toLowerCase())
      );
    }

    // Category filter
    if (filters.category) {
      filtered = filtered.filter(req => 
        req.category?.toLowerCase().includes(filters.category.toLowerCase())
      );
    }

    // Urgency filter
    if (filters.urgency) {
      filtered = filtered.filter(req => {
        const daysUntilClosure = req.days_until_closure || 30;
        const urgency = getUrgencyBadge(daysUntilClosure).text;
        return urgency.toLowerCase() === filters.urgency.toLowerCase();
      });
    }

    setFilteredRequests(filtered);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: '',
      requester: '',
      project: '',
      category: '',
      urgency: ''
    });
    setSearchTerm('');
    setFilteredRequests(pendingRequests);
  };

  // Format currency
  const formatCurrency = (amount, currency) => {
    // Currency symbols mapping
    const currencySymbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'TRY': '₺',
      'JPY': '¥',
      'CNY': '¥',
      'AED': 'د.إ',
      'SAR': '﷼',
      'AFN': '؋',
      'CHF': 'Fr',
      'CAD': 'C$',
      'AUD': 'A$',
      'NZD': 'NZ$',
      'HKD': 'HK$',
      'SGD': 'S$',
      'INR': '₹',
      'KRW': '₩',
      'RUB': '₽',
      'BRL': 'R$',
      'MXN': '$',
      'ZAR': 'R',
      'THB': '฿',
      'IDR': 'Rp',
      'PHP': '₱',
      'MYR': 'RM',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'PLN': 'zł',
      'CZK': 'Kč',
      'HUF': 'Ft'
    };
    
    const symbol = currencySymbols[currency] || currency;
    const formattedAmount = amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 });
    
    return `${symbol}${formattedAmount} ${currency}`;
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get urgency badge color based on days until closure
  const getUrgencyBadge = (daysUntilClosure) => {
    if (daysUntilClosure <= 3) {
      return { variant: 'destructive', text: 'Acil' };
    } else if (daysUntilClosure <= 7) {
      return { variant: 'default', text: 'Orta' };
    } else {
      return { variant: 'secondary', text: 'Normal' };
    }
  };

  // Check if user can approve a request
  const canApprove = (request) => {
    if (!user || !user.role) {
      console.log('❌ canApprove: Missing user data', { 
        hasUser: !!user, 
        hasRole: !!user?.role
      });
      return false;
    }
    
    const userRole = user.role.name;
    const userId = user.id;
    
    console.log('🔍 canApprove check:', { 
      userRole, 
      userId, 
      requesterId: request.requester_id,
      approverId: request.approver_id,
      isAdmin: ['Admin', 'Super Admin', 'Süper Admin'].includes(userRole),
      isApprover: request.approver_id === userId
    });
    
    // Admin and Super Admin can approve all requests
    if (['Admin', 'Super Admin', 'Süper Admin'].includes(userRole)) {
      console.log('✅ User is Admin/Super Admin - can approve');
      return true;
    }
    
    // Direct manager can approve (user is the approver_id of this request)
    if (request.approver_id && request.approver_id === userId) {
      console.log('✅ User is approver - can approve');
      return true;
    }
    
    console.log('❌ User cannot approve this request');
    return false;
  };

  // Handle quick approval (from list without opening modal)
  const handleQuickApproval = async (request, e) => {
    e.stopPropagation(); // Prevent card click
    
    try {
      setProcessing(true);
      
      const approvalData = {
        approved_amount: request.amount // Approve with original amount
      };

      await axios.post(`${API}/advance-requests/${request.id}/approve`, approvalData);
      
      toast.success('Avans talebi onaylandı');
      
      // Refresh the list
      fetchPendingRequests();
      
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.detail || 'Onaylama sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  // Handle approval
  const handleApproval = async () => {
    if (!selectedRequest) return;
    
    try {
      setProcessing(true);
      
      const approvalData = {};
      if (managerNotes.trim()) {
        approvalData.notes = managerNotes.trim();
      }
      // Always send approved amount (even if same as original)
      if (approvalAmount) {
        approvalData.approved_amount = parseFloat(approvalAmount);
      }

      await axios.post(`${API}/advance-requests/${selectedRequest.id}/approve`, approvalData);
      
      toast.success('Avans talebi onaylandı');
      setShowDetailModal(false);
      setSelectedRequest(null);
      setManagerNotes('');
      setApprovalAmount('');
      
      // Refresh the list
      fetchPendingRequests();
      
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.detail || 'Onaylama sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  // Handle rejection
  const handleRejection = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      toast.error('Red nedeni gereklidir');
      return;
    }
    
    try {
      setProcessing(true);
      
      const rejectionData = {
        reason: rejectionReason.trim()
      };
      if (managerNotes.trim()) {
        rejectionData.notes = managerNotes.trim();
      }

      await axios.post(`${API}/advance-requests/${selectedRequest.id}/reject`, rejectionData);
      
      toast.success('Avans talebi reddedildi. Talep edene email gönderildi.');
      setShowDetailModal(false);
      setSelectedRequest(null);
      setRejectionReason('');
      setManagerNotes('');
      
      // Refresh the list
      fetchPendingRequests();
      
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.response?.data?.detail || 'Reddetme sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  // Handle edit request
  const handleEdit = async () => {
    if (!selectedRequest) return;
    
    try {
      setProcessing(true);
      
      const updateData = {
        amount: parseFloat(editForm.amount),
        currency: editForm.currency,
        category: editForm.category,
        project: editForm.project,
        description: editForm.description,
        closure_days: parseInt(editForm.closure_days)
      };

      await axios.put(`${API}/advance-requests/${selectedRequest.id}`, updateData);
      
      toast.success('Avans talebi başarıyla güncellendi');
      setShowEditModal(false);
      setSelectedRequest(null);
      
      // Refresh the list
      fetchPendingRequests();
      
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error(error.response?.data?.detail || 'Güncelleme sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  // Handle delete request
  const handleDelete = async () => {
    if (!selectedRequest) return;
    
    try {
      setProcessing(true);

      await axios.delete(`${API}/advance-requests/${selectedRequest.id}`);
      
      toast.success('Avans talebi başarıyla silindi');
      setShowDeleteModal(false);
      setSelectedRequest(null);
      
      // Refresh the list
      fetchPendingRequests();
      
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error(error.response?.data?.detail || 'Silme sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  // Open edit modal
  const openEditModal = (request) => {
    setSelectedRequest(request);
    setEditForm({
      amount: request.amount,
      currency: request.currency || 'TRY',
      category: request.category || '',
      project: request.project || '',
      description: request.description || '',
      closure_days: request.closure_days || 30
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (request) => {
    setSelectedRequest(request);
    setShowDeleteModal(true);
  };

  // Open approval modal
  const openApprovalModal = (request) => {
    setSelectedRequest(request);
    setApprovalNotes('');
    setShowApprovalModal(true);
  };

  // Open detail modal
  const openDetailModal = (request) => {
    setSelectedRequest(request);
    setApprovalAmount(request.amount.toString());
    setManagerNotes('');
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        <span className="ml-2 text-gray-600">Onay bekleyen talepler yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pending-approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Onay Bekleyenler</h1>
          <p className="text-gray-600 mt-1">
            Onayınızı bekleyen {filteredRequests.length} adet avans talebi bulunmaktadır
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {pendingRequests.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-4 bg-orange-50 border-orange-200">
            <div className="flex items-center">
              <ClockIcon className="w-8 h-8 text-orange-600 mr-3" />
              <div>
                <p className="text-sm text-orange-700">Toplam Bekleyen</p>
                <p className="text-lg font-bold text-orange-900">
                  {filteredRequests.length} Talep
                </p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center">
              <BanknoteIcon className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-sm text-blue-700">Toplam Bekleyen Tutar</p>
                <p className="text-lg font-bold text-blue-900">
                  {(() => {
                    // Get base currency from first request (backend sends it)
                    const baseCurrency = filteredRequests[0]?.base_currency || user?.company?.base_currency || 'TRY';
                    
                    // Sum amounts in base currency (backend converts each)
                    const totalInBase = filteredRequests.reduce((sum, req) => {
                      return sum + (req.amount_in_base_currency || req.amount);
                    }, 0);
                    
                    return formatCurrency(totalInBase, baseCurrency);
                  })()}
                </p>
                {(() => {
                  const currencies = [...new Set(filteredRequests.map(r => r.currency))];
                  if (currencies.length > 1) {
                    return (
                      <p className="text-xs text-blue-600 mt-1">
                        {currencies.length} farklı para biriminden
                      </p>
                    );
                  }
                })()}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Collapsible Filter Bar */}
      <Card>
        {/* Filter Header */}
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center gap-2">
            <FilterIcon className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filtreler</h3>
            <span className="text-sm text-gray-500">
              ({filteredRequests.length} / {pendingRequests.length} talep)
            </span>
          </div>
          <div className="flex items-center gap-2">
            {(Object.values(filters).some(value => value !== '') || searchTerm) && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  clearFilters();
                }}
                variant="outline"
                size="sm"
                className="gap-1"
              >
                <XIcon className="w-4 h-4" />
                Temizle
              </Button>
            )}
            {showFilters ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-600" />
            )}
          </div>
        </div>

        {/* Collapsible Filter Content */}
        {showFilters && (
          <div className="border-t border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Arama</label>
                <Input
                  type="text"
                  placeholder="Talep eden, proje, kategori ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({...prev, dateFrom: e.target.value}))}
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({...prev, dateTo: e.target.value}))}
                />
              </div>

              {/* Urgency Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aciliyet</label>
                <select
                  value={filters.urgency}
                  onChange={(e) => setFilters(prev => ({...prev, urgency: e.target.value}))}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="">Tümü</option>
                  <option value="acil">Acil</option>
                  <option value="orta">Orta</option>
                  <option value="normal">Normal</option>
                </select>
              </div>

              {/* Min Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min. Tutar</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.minAmount}
                  onChange={(e) => setFilters(prev => ({...prev, minAmount: e.target.value}))}
                />
              </div>

              {/* Max Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max. Tutar</label>
                <Input
                  type="number"
                  placeholder="∞"
                  value={filters.maxAmount}
                  onChange={(e) => setFilters(prev => ({...prev, maxAmount: e.target.value}))}
                />
              </div>

              {/* Requester */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Talep Eden</label>
                <Input
                  type="text"
                  placeholder="İsim ara..."
                  value={filters.requester}
                  onChange={(e) => setFilters(prev => ({...prev, requester: e.target.value}))}
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                <Input
                  type="text"
                  placeholder="Kategori ara..."
                  value={filters.category}
                  onChange={(e) => setFilters(prev => ({...prev, category: e.target.value}))}
                />
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Modern Card Layout */}
      {filteredRequests.length === 0 ? (
        <Card className="p-8 text-center">
          <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || Object.values(filters).some(value => value !== '') ? 'Sonuç bulunamadı' : 'Onay bekleyen talep yok'}
          </h3>
          <p className="text-gray-600">
            {searchTerm || Object.values(filters).some(value => value !== '') ? 'Arama kriterlerinizle eşleşen talep bulunamadı.' : 'Şu anda onayınızı bekleyen herhangi bir avans talebi bulunmamaktadır.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => {
            const urgencyBadge = getUrgencyBadge(request.days_until_closure);
            // Check if this is user's own request
            const isOwnRequest = request.requester_id === user?.id;
            // Check if user can approve this request (managers/admins)
            const hasApprovalPermission = canApprove(request);
            // Check if user is Admin/Super Admin
            const isAdmin = user?.role && ['Admin', 'Super Admin', 'Süper Admin'].includes(user.role.name);
            // Can show detail and approve if:
            // - Has permission AND (not own request OR is Admin/Super Admin)
            const canShowDetailAndApprove = hasApprovalPermission && (!isOwnRequest || isAdmin);
            
            return (
              <Card 
                key={request.id} 
                className={`p-6 transition-all duration-200 border-l-4 ${
                  urgencyBadge.variant === 'destructive' ? 'border-l-red-500' :
                  urgencyBadge.variant === 'default' ? 'border-l-yellow-500' : 'border-l-green-500'
                } ${canShowDetailAndApprove ? 'hover:shadow-lg cursor-pointer' : ''}`} 
                onClick={canShowDetailAndApprove ? () => openDetailModal(request) : undefined}
              >
                <div className="max-w-4xl">
                  <div className="flex items-start justify-between">
                    {/* Left Section - User and Basic Info */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center">
                          <UserIcon className="w-6 h-6 text-teal-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {request.requester_name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {request.requester_email}
                          </p>
                          {request.advance_number && (
                            <Tooltip content={request.advance_number}>
                              <p className="text-xs text-gray-400 font-mono cursor-help hover:text-blue-500 transition-colors">
                                {formatAdvanceNumber(request.advance_number)}
                              </p>
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      {/* Content Row */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                        
                        {/* Category */}
                        <div className="flex items-center space-x-2">
                          <FolderIcon className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500">Kategori</p>
                            <Badge variant="secondary" className="text-xs mt-1">
                              {request.category}
                            </Badge>
                          </div>
                        </div>

                        {/* Request Date */}
                        <div className="flex items-center space-x-2">
                          <CalendarIcon className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500">Talep Tarihi</p>
                            <p className="text-sm font-medium text-gray-700 mt-1">
                              {formatDate(request.request_date)}
                            </p>
                          </div>
                        </div>

                        {/* Closure Time */}
                        <div className="flex items-center space-x-2">
                          <ClockIcon className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500">Kapama Süresi</p>
                            <p className={`text-sm font-medium mt-1 ${
                              request.days_until_closure <= 3 ? 'text-red-600' :
                              request.days_until_closure <= 7 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {request.days_until_closure > 0 
                                ? `${request.days_until_closure} gün`
                                : 'Gecikmiş'
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Project */}
                      {request.project && (
                        <div className="flex items-center space-x-2">
                          <FolderIcon className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-xs text-gray-500">Proje</p>
                            <p className="text-sm font-medium text-gray-700 mt-1">
                              {request.project}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Description */}
                      {request.description && (
                        <div className="mt-3">
                          <div className="flex items-start space-x-2">
                            <MessageSquareIcon className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Açıklama</p>
                              <p className="text-sm text-gray-700">
                                {request.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {request.notes && (
                        <div className="mt-2">
                          <div className="flex items-start space-x-2">
                            <MessageSquareIcon className="w-4 h-4 text-blue-400 mt-0.5" />
                            <div>
                              <p className="text-xs text-blue-600 mb-1">Notlar</p>
                              <p className="text-sm text-blue-700">
                                {request.notes}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Section - Amount, Status and Action Info */}
                    <div className="text-right space-y-3 ml-6">
                      {/* Large Amount */}
                      <div className="text-2xl font-bold text-gray-900">
                        {formatCurrency(request.amount, request.currency)}
                      </div>
                      
                      {/* Status Badges */}
                      <div className="space-y-2">
                        <Badge variant={urgencyBadge.variant}>
                          {urgencyBadge.text}
                        </Badge>
                        <div>
                          <Badge className="bg-yellow-100 text-yellow-800 px-3 py-1.5">
                            <ClockIcon className="w-4 h-4 mr-1" />
                            Onay Bekliyor
                          </Badge>
                        </div>
                      </div>

                      {/* Action Buttons - Only for own requests */}
                      {request.requester_id === user?.id && (
                        <div className="flex space-x-2 justify-end">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(request);
                            }}
                            className="flex items-center gap-1"
                          >
                            <EditIcon className="w-3 h-3" />
                            Düzenle
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(request);
                            }}
                            className="flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <TrashIcon className="w-3 h-3" />
                            Sil
                          </Button>
                        </div>
                      )}

                      {/* Approve Button - Only for managers/admins who can approve AND not own request */}
                      {canShowDetailAndApprove && (
                        <div className="mt-2">
                          <Button 
                            size="sm"
                            onClick={(e) => handleQuickApproval(request, e)}
                            disabled={processing}
                            className="w-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2"
                          >
                            <CheckIcon className="w-4 h-4" />
                            Onayla
                          </Button>
                        </div>
                      )}

                      {/* Detail Badge - Only for managers/admins who can approve AND not own request */}
                      {canShowDetailAndApprove && (
                        <div className="mt-2">
                          <Badge variant="outline" className="px-3 py-1.5 w-full text-center block">
                            Detay
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Talep Detayı</DialogTitle>
            <p className="text-sm text-gray-600">Talebi inceleyin ve karar verin</p>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6 py-4">
              {/* Header with ID and Amount */}
              <div className="flex justify-between items-start border-b pb-4">
                <div>
                  <h2 className="text-lg font-semibold">{selectedRequest.category}</h2>
                  {selectedRequest.advance_number ? (
                    <Tooltip content={selectedRequest.advance_number}>
                      <p className="text-sm text-gray-600 font-mono cursor-help hover:text-blue-500 transition-colors">
                        {formatAdvanceNumber(selectedRequest.advance_number)}
                      </p>
                    </Tooltip>
                  ) : (
                    <p className="text-sm text-gray-600 font-mono">
                      Numara yükleniyor...
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">İlk Talep Edilen Tutar</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(selectedRequest.amount, selectedRequest.currency)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Request Info */}
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3">Talep Eden</h3>
                    <p className="text-lg font-medium">{selectedRequest.requester_name}</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3">Kategori</h3>
                    <Badge variant="secondary" className="text-sm">
                      {selectedRequest.category}
                    </Badge>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3">Tarih</h3>
                    <p className="text-sm">
                      {new Date(selectedRequest.request_date).toLocaleDateString('tr-TR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                      {selectedRequest.closure_date && (
                        <span> - {new Date(selectedRequest.closure_date).toLocaleDateString('tr-TR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        })}</span>
                      )}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3">Açıklama</h3>
                    <p className="text-gray-700">
                      {selectedRequest.description}
                    </p>
                  </div>
                </div>

                {/* Right Column - Approval Form */}
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-900 mb-3">Onaylanacak Tutar *</h3>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="number"
                        value={approvalAmount}
                        onChange={(e) => setApprovalAmount(e.target.value)}
                        className="text-lg font-semibold"
                        step="0.01"
                        min="0"
                      />
                      <span className="text-lg font-medium text-gray-700">
                        {selectedRequest.currency}
                      </span>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Yönetici Notları (Opsiyonel)
                    </h3>
                    <Textarea
                      value={managerNotes}
                      onChange={(e) => setManagerNotes(e.target.value)}
                      placeholder="Talep ile ilgili notlarınızı buraya ekleyin..."
                      rows={4}
                      className="w-full"
                    />
                  </div>

                  {/* Rejection Reason Field */}
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h3 className="font-semibold text-red-900 mb-3">
                      Red Nedeni (Reddetmek için gerekli)
                    </h3>
                    <Textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Avans talebini reddetmek istiyorsanız nedeni buraya yazın..."
                      rows={3}
                      className="w-full"
                    />
                    <p className="text-xs text-red-600 mt-2">
                      * Bu alan doldurulursa 'Reddet' butonu aktif olur ve talep edene email gönderilir.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between pt-6 border-t">
            <div className="flex-1 mr-4">
              <Button 
                onClick={handleRejection}
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50 w-full"
                disabled={processing || !rejectionReason.trim()}
              >
                {processing ? 'Reddediliyor...' : '✗ Reddet'}
              </Button>
            </div>
            <div className="flex-1">
              <Button 
                onClick={handleApproval}
                disabled={processing || !approvalAmount}
                className="btn-primary w-full"
              >
                {processing ? 'Onaylanıyor...' : '✓ Onayla'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Avans Talebini Düzenle</DialogTitle>
            <p className="text-sm text-gray-600">Onay bekleyen talebinizi güncelleyebilirsiniz</p>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-amount">Tutar</Label>
                  <AmountInput
                    id="edit-amount"
                    value={editForm.amount}
                    onChange={(value) => setEditForm({...editForm, amount: value})}
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-currency">Para Birimi</Label>
                  <select 
                    id="edit-currency"
                    value={editForm.currency}
                    onChange={(e) => setEditForm({...editForm, currency: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="edit-category">Kategori</Label>
                <Input
                  id="edit-category"
                  value={editForm.category}
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                  placeholder="Kategori"
                />
              </div>

              <div>
                <Label htmlFor="edit-project">Proje</Label>
                <Input
                  id="edit-project"
                  value={editForm.project}
                  onChange={(e) => setEditForm({...editForm, project: e.target.value})}
                  placeholder="Proje adı"
                />
              </div>

              <div>
                <Label htmlFor="edit-closure-days">Kapama Süresi (Gün)</Label>
                <select 
                  id="edit-closure-days"
                  value={editForm.closure_days}
                  onChange={(e) => setEditForm({...editForm, closure_days: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value={15}>15 Gün</option>
                  <option value={30}>30 Gün</option>
                  <option value={60}>60 Gün</option>
                </select>
              </div>

              <div>
                <Label htmlFor="edit-description">Açıklama</Label>
                <Textarea
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  placeholder="Açıklama..."
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowEditModal(false)}
              disabled={processing}
            >
              İptal
            </Button>
            <Button 
              onClick={handleEdit}
              disabled={processing || !editForm.amount}
            >
              {processing ? 'Güncelleniyor...' : 'Güncelle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Avans Talebini Sil</DialogTitle>
            <p className="text-sm text-gray-600">Bu işlem geri alınamaz</p>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-red-900 mb-2">
                  Silinecek Talep:
                </h4>
                <p className="text-sm text-red-700">
                  <strong>Tutar:</strong> {formatCurrency(selectedRequest.amount, selectedRequest.currency)}
                </p>
                <p className="text-sm text-red-700">
                  <strong>Kategori:</strong> {selectedRequest.category}
                </p>
                {selectedRequest.advance_number && (
                  <p className="text-sm text-red-700">
                    <strong>Avans No:</strong> {selectedRequest.advance_number}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mt-4">
                Bu avans talebini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteModal(false)}
              disabled={processing}
            >
              İptal
            </Button>
            <Button 
              onClick={handleDelete}
              disabled={processing}
              variant="destructive"
            >
              {processing ? 'Siliniyor...' : 'Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingApprovals;